import React, { useEffect, useMemo, useState } from 'react'
import useElementSize from '../../../hooks/useElementSize'
import Text from '../../atoms/text/Text'

import Container from '../../atoms/page/Page'
import SimpleModal from '../../organisms/standard-modal/SimpleModal'
import { RENDERED_DIMENSIONS } from './components/ReportingPagePreviewTile'


import InlineGroup from '../../atoms/inlinegroup/InlineGroup'
import Toggle from '../../atoms/toggle/Toggle'
import RoundDropdown from '../../organisms/round-dropdown/RoundDropdown'
import ReportingTemplatePreviewContent from './components/ReportingTemplatePreviewContent'
import SimulationDropdown from '../../organisms/simulation-dropdown/SimulationDropdown'
import VerticalGroup from '../../atoms/verticalgroup/VerticalGroup'
import Icon from '../../atoms/icon/Icon'

interface Props {
  isOpen: boolean
  onClose: () => void
  reportingTemplateId: string
  simulationId?: string
  showSimulationDropdown?: boolean
  brandingId?: string
  maxRounds?: number
}

const containerPadding = 32
const innerContainerPadding = 60

const ReportingTemplatePreviewModal: React.FC<Props> = ({
  reportingTemplateId,
  simulationId,
  brandingId,
  maxRounds,
  isOpen,
  onClose,
  showSimulationDropdown,
}) => {
  const [mode, setMode] = useState<'thumbnails' | 'fullscreen'>('thumbnails')
  const [round, setRound] = useState(1)
  const [containerRef, { height: containerHeight, width: containerWidth }] = useElementSize()
  const [headingRef, { height: headingHeight }] = useElementSize()
  const [simId, setSimulationId] = useState<string | null>(simulationId ??  null)

  useEffect(() => {
    if (simulationId) {
      setSimulationId(simulationId)
    }
  }, [simulationId])

  const {previewHeight, previewWidth, availableHeight, availableWidth} = useMemo(() => {
    if (containerHeight && headingHeight && containerWidth) {
      const heightAvailable = containerHeight - headingHeight - containerPadding - innerContainerPadding
      const widthAvailable = containerWidth - containerPadding - innerContainerPadding

      const scale = Math.min(
        1,
        Math.min(widthAvailable/RENDERED_DIMENSIONS.width, heightAvailable/RENDERED_DIMENSIONS.height)
      )

      return {
        previewHeight: scale * RENDERED_DIMENSIONS.height,
        previewWidth: scale * RENDERED_DIMENSIONS.width,
        availableHeight: heightAvailable,
        availableWidth: widthAvailable
      }
    }
    return { previewHeight: 0, previewWidth: 0, availableHeight: 0, availableWidth: 0 }
  }, [containerHeight, containerWidth, headingHeight])



  return <SimpleModal
    size="scale"
    isOpen={isOpen}
    showCloseButton
    onClose={onClose}
  >
    <Container ref={containerRef} className='reporting-template-preview' noOverflow>
      <InlineGroup verticalCenter spread>
        <h2 ref={headingRef}>Reporting Template Preview</h2>
        <InlineGroup spaceLeft={2} verticalCenter>
          {showSimulationDropdown && <InlineGroup spaceLeft={2} verticalCenter>
            <Text size="sm" bold>Simulation</Text>
            <SimulationDropdown
              simulationId={simId ?? undefined}
              onSimulationSelected={(simulation: API.SimulationResponse) => setSimulationId(simulation.id)}
            />
          </InlineGroup>}
          <InlineGroup spaceLeft={2} verticalCenter>
            <Text>Round:</Text>
            <RoundDropdown value={round} onChange={(selected) => setRound(parseInt(selected.value))} min={1} max={maxRounds} tiny/>
          </InlineGroup>
          <InlineGroup spaceLeft={2} verticalCenter>
            <Text>Fullscreen:</Text>
            <Toggle checked={mode === 'fullscreen'} onUpdate={() => {
              setMode(mode => mode === 'fullscreen' ? 'thumbnails' : 'fullscreen')
            }} />
          </InlineGroup>
        </InlineGroup>
      </InlineGroup>
      {!simId && <VerticalGroup center verticalCenter full>
        <Icon type='simulations' size={24} colour='secondaryDark4' />
        <Text colour='secondaryDark3' size='xl'>Please select a simulation to preview</Text>
      </VerticalGroup>}
      {simId && <ReportingTemplatePreviewContent
        reportingTemplateId={reportingTemplateId}
        simulationId={simId}
        brandingId={brandingId}
        round={round}
        mode={mode}
        setMode={setMode}
        previewHeight={previewHeight}
        previewWidth={previewWidth}
        availableHeight={availableHeight}
        availableWidth={availableWidth}
      />}
    </Container>
  </SimpleModal>
}

export default ReportingTemplatePreviewModal
